import React, { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import axios from 'axios';
import VariableProcessor from 'mustache'
import './LabGuide.css'
import { Button, Popup } from 'semantic-ui-react';
import remarkGfm from 'remark-gfm'
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import remarkGemoji from 'remark-gemoji';
import javascript from 'react-syntax-highlighter/dist/cjs/languages/prism/javascript';
import json from 'react-syntax-highlighter/dist/cjs/languages/prism/json';
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Link } from '@okta/odyssey-react-mui';
import rehypeRaw from "rehype-raw";
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { remarkAlert } from 'remark-github-blockquote-alert'
import 'remark-github-blockquote-alert/alert.css'

SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('json', json);

const LabGuide = ({ contentLink, documentRef, variableData = {} }) => {

    const { t } = useTranslation();
    const [contentSource, setContentSource] = useState()
    const [content, setContent] = useState()
    const r = new RegExp('^(?:[a-z+]+:)?//', 'i');

    useEffect(() => {
        const variables = processVariables(variableData)
        let contentUrl
        if(contentLink){
            if(typeof contentLink === 'string'){
                contentUrl = contentLink
            } else {
                if(contentLink[i18n.language]){
                    contentUrl = contentLink[i18n.language]
                }
                else{
                    contentUrl = contentLink['en']
                }
            }
            setContentSource(contentUrl);
            axios.get(contentUrl).then((response) => { setContent(VariableProcessor.render(response.data, variables)) }).catch(err => console.log(err))
        }
    }, [contentLink, variableData])

    function processVariables(variableData) {
        let variables = {}
        if (variableData) {
            if (typeof variableData.idp != "undefined") { 
                variables.idp = variableData.idp
                if(variableData.idp.type === "workforce-identity"){
                    switch (variableData.idp.variant) {
                        case "production":
                            variables.idp.tenantDomain = variableData.idp.name + '.okta.com';
                            break;
                        case "preview":
                            variables.idp.tenantDomain = variableData.idp.name + '.oktapreview.com';
                            break;
                        default:
                            variables.idp.tenantDomain = variableData.idp.name + '.okta.com';
                    }
                } else if( variableData.idp.type === "customer-identity" ){
                    variableData.idp.tenantDomain = variableData.idp.name + '.cic-demo-platform.auth0app.com'
                }
             }
            if (typeof variableData.demo != "undefined") {
                const instances = []
                if (typeof variableData.demo.app_instances != "undefined") { instances.push(...variableData.demo.app_instances) }
                if (typeof variableData.demo.resource_instances != "undefined") { instances.push(...variableData.demo.resource_instances) }
                for (let instance of instances) {
                    if (typeof instance.instanceDetails.label != "undefined") {
                        const instanceKey = instance.instanceDetails.label.replace(/\s/g, "")
                        const instanceID = instance.componentId
                        variables[instanceKey] = (typeof variables[instanceKey] === 'undefined') ? {} : variables[instanceKey]
                        variables[instanceID] = (typeof variables[instanceID] === 'undefined') ? {} : variables[instanceID]
                        variables[instanceKey].label = instance.instanceDetails.label
                        variables[instanceID].label = instance.instanceDetails.label
                        if (typeof instance.instanceDetails.credentials != "undefined") {
                            variables[instanceKey].credentials = instance.instanceDetails.credentials
                            variables[instanceID].credentials = instance.instanceDetails.credentials
                        }
                        if (typeof instance.instanceDetails.deploymentData != "undefined") {
                            for (const key in instance.instanceDetails.deploymentData) {
                                variables[instanceKey][key.replace(/\s/g, "")] = instance.instanceDetails.deploymentData[key.replace(/\s/g, "")]
                                variables[instanceID][key.replace(/\s/g, "")] = instance.instanceDetails.deploymentData[key.replace(/\s/g, "")]
                            }
                        }
                        if (typeof instance.instanceDetails.deploymentHistory != "undefined") {
                            variables[instanceKey].history = instance.instanceDetails.deploymentHistory
                            variables[instanceID].history = instance.instanceDetails.deploymentHistory
                        }
                    }
                }
            }
        }
        return variables
    }

    function flatten(text, child) {
        return typeof child === 'string'
            ? text + child
            : React.Children.toArray(child.props.children).reduce(flatten, text)
    }

    function HeadingRenderer(props) {
        var children = React.Children.toArray(props.children)
        var text = children.reduce(flatten, '')
        var slug = text.toLowerCase().replace(/\W/g, '-')
        return React.createElement('h' + props.level, { id: slug }, props.children)
    }
    function linkRenderer(props) {
        return <Link href={props.href} target='_blank'>{props.children}</Link>
    }

    function codeRenderer({ node, inline, className, ...props }) {
        const hasLang = /language-(\w+)/.exec(className || 'text');
        return (
            <div className='codeWrapper'>
                <SyntaxHighlighter
                    style={oneDark}
                    language={hasLang ? hasLang[1] : 'text'}
                    PreTag="div"
                    className="codeStyle"
                    showLineNumbers={false}
                    wrapLines={true}
                    wrapLongLines={true}
                    children={props.children}
                />
                <Popup
                    trigger={<CopyToClipboard text={props.children}><Button inverted className='copyBtn' size='mini' icon='copy' /></CopyToClipboard>}
                    closeOnTriggerMouseLeave={true}
                    content={t('copied')}
                    on='click'
                />
            </div>
        )
    }

    function relativeImageExpander(props) {
        var clone = { ...props }
        if (!r.test(props.src)) {
            clone.src = new URL(props.src, contentSource).href
        }
        if(clone.src !== "https://cdn.demo.okta.com/images/okta-icon.png"){
        return (<PhotoProvider>
            <PhotoView src={clone.src}>
              <img src={clone.src} alt="" className='inlineImg' />
            </PhotoView>
          </PhotoProvider>)
        } else {
            return (<img src={clone.src} alt="" className='infoImg' />)
        }
    }

    return (
        <div id="labguide" ref={documentRef}>
            <ReactMarkdown rehypePlugins={[rehypeRaw]} components={{ h1: HeadingRenderer, h2: HeadingRenderer, code: codeRenderer, img: relativeImageExpander, a: linkRenderer}} className="docframe" remarkPlugins={[remarkGfm, remarkGemoji, remarkAlert]}>{content}</ReactMarkdown>
        </div>
    );
};
export default LabGuide;

